import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp from "../../firebase/credenciales";
import {
  canonicalizeContractVariableKey,
  canonicalizePlaceholdersInText,
  CONTRACT_VARIABLES_PROMPT,
} from "./contractVariableSchema";
const functions = getFunctions(firebaseApp, "us-central1");

const escapeRegExp = (text) => String(text || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const ensureClauseHeadingInContent = (title, content) => {
  const rawTitle = String(title || "").trim();
  const rawContent = String(content || "").trim();

  if (!rawTitle) return rawContent;
  if (/introducci[oó]n/i.test(rawTitle)) return rawContent;
  if (!rawContent) return rawTitle;

  const titleRegex = new RegExp(`^\\s*${escapeRegExp(rawTitle)}\\s*(:|\\b)`, "i");
  if (titleRegex.test(rawContent)) return rawContent;

  // Si el contenido ya empieza con un encabezado ordinal, no forzar prefijo adicional.
  if (
    /^\s*(PRIMER[OA]|SEGUND[OA]|TERCER[OA]|CUART[OA]|QUINT[OA]|SEX(T|T[OA])|S[ÉE]PTIM[OA]|OCTAV[OA]|NOVEN[OA]|D[ÉE]CIM[AO]|UND[ÉE]CIM[AO]|DUOD[ÉE]CIM[AO]|DECIMO\w*)\s*:/i.test(
      rawContent
    )
  ) {
    return rawContent;
  }

  const separator = /[:.]$/.test(rawTitle) ? " " : ": ";
  return `${rawTitle}${separator}${rawContent}`;
};

const normalizeClauseContentFromModel = (rawContent) => {
  const source = String(rawContent || "");
  if (!source.trim()) return "";

  const paragraphBlocks = source
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  const normalizedBlocks = paragraphBlocks.map((block) => {
    const lines = block
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (!lines.length) return "";

    const allListLike = lines.every((line) => /^([0-9]+[.)]|[IVXLCDM]+[.)]|[-*•])\s+/i.test(line));
    if (allListLike) return lines.join("\n");

    const allVeryShort = lines.every((line) => line.length <= 45);
    const hasHeadingLine = lines.some(
      (line) =>
        line.length <= 120 &&
        !/[.;:]$/.test(line) &&
        /^(CL[ÁA]USULA|PRIMER[OA]|SEGUND[OA]|TERCER[OA]|CUART[OA]|QUINT[OA]|SEX(T|T[OA])|S[ÉE]PTIM[OA]|OCTAV[OA]|NOVEN[OA]|D[ÉE]CIM[OA]|UND[ÉE]CIM[OA]|DUOD[ÉE]CIM[OA]|DECIMO|INTRODUCCI[ÓO]N)/i.test(
          line
        )
    );

    if (allVeryShort && !hasHeadingLine) {
      return lines.join(" ");
    }

    return lines
      .map((line, idx) => {
        if (idx === 0) return line;
        const prev = lines[idx - 1];
        const currentLooksList = /^([0-9]+[.)]|[IVXLCDM]+[.)]|[-*•])\s+/i.test(line);
        const prevEndsSentence = /[.;:]$/.test(prev);
        if (currentLooksList || prevEndsSentence) return `\n${line}`;
        return ` ${line}`;
      })
      .join("");
  });

  return normalizedBlocks.filter(Boolean).join("\n\n").trim();
};

const normalizeResult = (parsed) => {
  const clauses = Array.isArray(parsed?.clauses)
    ? parsed.clauses.map((c, index) => ({
        id: c.id || `clausula-${Date.now()}-${index}`,
        title: c.title || `Cláusula ${index + 1}`,
        content: canonicalizePlaceholdersInText(
          normalizeClauseContentFromModel(ensureClauseHeadingInContent(c.title || "", c.content || ""))
        ),
      }))
    : [];

  const extractedVars = new Set();

  clauses.forEach((c) => {
    const matches = c.content.match(/{{\s*([a-zA-Z0-9_]+)\s*}}/g) || [];
    matches.forEach((m) => extractedVars.add(m.replace(/{{|}}|\s/g, "")));
  });

  const variables = Array.isArray(parsed?.variables)
    ? parsed.variables
        .map((v, index) => {
          const sourceText = v.sourceText || v.textoOriginal || "";
          const label = v.label || v.etiqueta || v.key || sourceText || `variable_${index + 1}`;
          const normalizedKey = canonicalizeContractVariableKey(v.key || v.nombre || label || sourceText);

          return {
            key: normalizedKey || `variable_${index + 1}`,
            label,
            sourceText,
            confirmed: v.confirmed !== false,
          };
        })
        .filter((v) => v.key)
    : [];

  extractedVars.forEach((key) => {
    const existing = variables.find((v) => v.key === key);
    if (!existing) {
      variables.push({
        key,
        label: key,
        sourceText: "",
        confirmed: true,
      });
    }
  });

  return {
    templateName: parsed?.templateName || "Contrato sin nombre",
    contractType: parsed?.contractType || "GENERAL",
    clauses,
    variables,
    legalAlerts: Array.isArray(parsed?.legalAlerts) ? parsed.legalAlerts : [],
  };
};

export const parseContractTemplateWithGemini = async ({ contractText, companyContext = {} }) => {
  const callable = httpsCallable(functions, "parseContractTemplateWithDeepseek");
  const result = await callable({
    contractText,
    companyContext,
    variablesPrompt: CONTRACT_VARIABLES_PROMPT,
  });
  return normalizeResult(result?.data || {});
};

const contractTemplateService = {
  parseContractTemplateWithGemini,
};

export default contractTemplateService;
